<template>
  <div ref="shell" class="ads-item" :class="{ 'ads-loading': isLoading, 'ads-item--collapsed': shouldCollapse }"
    :data-ad-status="adStatus" :data-ad-state="adState">
    <div class="ads-borded">
      <div v-show="!shouldCollapse" class="ads-borded-title">
        Advertisement
      </div>
      <ins ref="ads" v-bind="ads" @error="handleAdError" />
    </div>
    <div v-show="showDebug" class="ad-msg">
      <div>Status: {{ adStatus }}</div>
      <div>Load Time: {{ loadTime }}ms</div>
      <div>{{ ads }}</div>
    </div>
  </div>
</template>

<script>
const AD_STATUS = {
  PENDING: 'pending',
  FILLED: 'filled',
  UNFILLED: 'unfilled',
  ERROR: 'error'
}

const NO_FILL_GRACE_MS = 1500
const COLLAPSE_STYLES = {
  height: '0',
  minHeight: '0',
  maxHeight: '0',
  margin: '0',
  padding: '0',
  overflow: 'hidden',
  visibility: 'hidden'
}

export default {
  props: {
    ads: {
      type: [String, Object],
      required: false,
      default: null
    },
    showDebug: {
      type: Boolean,
      required: true
    },
    timeout: {
      type: Number,
      default: 5000
    }
  },

  data () {
    return {
      isAdFilled: false,
      isLoading: true,
      adStatus: AD_STATUS.PENDING,
      adState: AD_STATUS.PENDING,
      loadTime: 0,
      loadStartTime: null,
      timeoutTimer: null,
      noFillTimer: null,
      statusObserver: null,
      errorCount: 0,
      maxErrors: 3,
      renderTimeout: null,
      shouldShowAd: true
    }
  },

  watch: {
    ads: {
      immediate: true,
      handler (newAds) {
        if (newAds) {
          this.resetState()
          this.initAd()
        }
      }
    }
  },

  mounted () {
    this.ads && this.initAd()
    // 通知父组件重新观察广告（如果父组件使用了adMixin）
    this.$parent.observeAds && this.$parent.observeAds()
  },

  beforeDestroy () {
    this.clearTimers()
    this.statusObserver?.disconnect()
  },

  computed: {
    shouldCollapse () {
      return !this.showDebug && [AD_STATUS.UNFILLED, AD_STATUS.ERROR].includes(this.adStatus)
    }
  },

  methods: {
    initAd () {
      this.loadStartTime = Date.now()
      // 移除超时倒计时，因为使用了懒加载（adMixin），广告可能在很久之后才加载
      // this.startTimeout() 
      this.observeAdStatus()
    },

    observeAdStatus () {
      const ads = this.$refs.ads
      if (!ads) return

      this.statusObserver?.disconnect()
      this.statusObserver = new MutationObserver(() => this.syncAdRuntime())
      this.statusObserver.observe(ads, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['data-ad-status', 'data-adsbygoogle-status', 'style']
      })
      this.syncAdRuntime()
    },

    syncAdRuntime () {
      const ads = this.$refs.ads
      if (!ads) return

      const status = ads.getAttribute('data-ad-status') || AD_STATUS.PENDING
      const googleStatus = ads.getAttribute('data-adsbygoogle-status') || ''
      const hasVisibleAd = this.hasVisibleAdIframe()
      const isNoFill = [AD_STATUS.UNFILLED, AD_STATUS.ERROR].includes(status)

      if (isNoFill) {
        this.setAdRuntime(status, false)
      } else if (hasVisibleAd) {
        this.setAdRuntime(AD_STATUS.FILLED, true)
      } else if (googleStatus === 'done' || status === AD_STATUS.FILLED) {
        if (this.adStatus === AD_STATUS.UNFILLED) return
        this.scheduleNoFillCheck()
      } else {
        this.setAdRuntime(status, false)
      }
    },

    hasVisibleAdIframe () {
      const ads = this.$refs.ads
      if (!ads) return false

      return Array.from(ads.querySelectorAll('iframe')).some(iframe => {
        const rect = iframe.getBoundingClientRect()
        const style = window.getComputedStyle(iframe)
        return rect.width > 1 && rect.height > 1 && style.display !== 'none' && style.visibility !== 'hidden'
      })
    },

    scheduleNoFillCheck () {
      if (this.noFillTimer || this.hasVisibleAdIframe()) return

      this.noFillTimer = setTimeout(() => {
        this.noFillTimer = null
        if (!this.hasVisibleAdIframe()) {
          this.setAdRuntime(AD_STATUS.UNFILLED, false)
        }
      }, NO_FILL_GRACE_MS)
    },

    setAdRuntime (status, hasVisibleAd) {
      this.clearNoFillTimer()
      this.adStatus = status || AD_STATUS.PENDING
      this.isAdFilled = hasVisibleAd
      this.isLoading = !hasVisibleAd && ![AD_STATUS.UNFILLED, AD_STATUS.ERROR].includes(this.adStatus)
      this.adState = this.shouldCollapse ? 'collapsed' : (hasVisibleAd ? AD_STATUS.FILLED : this.adStatus)

      if (this.shouldCollapse) {
        this.applyCollapseGuard()
      } else {
        this.clearCollapseGuard()
      }

      if (hasVisibleAd) {
        this.loadTime = Date.now() - this.loadStartTime
        this.clearTimeout()
        this.$emit('adLoaded', {
          status: AD_STATUS.FILLED,
          loadTime: this.loadTime
        })
      }
    },

    applyCollapseGuard () {
      this.applyImportantStyles(this.$refs.shell)
      this.applyImportantStyles(this.$refs.shell?.querySelector('.ads-borded'))
    },

    clearCollapseGuard () {
      this.clearImportantStyles(this.$refs.shell)
      this.clearImportantStyles(this.$refs.shell?.querySelector('.ads-borded'))
    },

    applyImportantStyles (element) {
      if (!element) return
      Object.entries(COLLAPSE_STYLES).forEach(([property, value]) => {
        element.style.setProperty(property.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`), value, 'important')
      })
    },

    clearImportantStyles (element) {
      if (!element) return
      Object.keys(COLLAPSE_STYLES).forEach(property => {
        element.style.removeProperty(property.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`))
      })
    },

    handleAdError (error) {
      this.errorCount++

      if (process.env.NODE_ENV === 'development') {
        console.warn(`Ad load attempt ${this.errorCount}/${this.maxErrors}: ${error.message}`)
      }

      this.$eventrack?.('ad_load_error', 'error', {
        errorType: error.message,
        attempt: this.errorCount,
        adSlot: this.ads?.['data-ad-slot']
      })

      if (this.errorCount < this.maxErrors) {
        this.retryAd()
      } else {
        this.adStatus = AD_STATUS.ERROR
        this.setAdRuntime(AD_STATUS.ERROR, false)
        this.shouldShowAd = false
        this.$emit('adError', {
          error: error.message,
          attempts: this.errorCount,
          adSlot: this.ads?.['data-ad-slot']
        })
      }
    },

    retryAd () {
      this.clearTimers()
      this.renderTimeout = setTimeout(() => {
        this.initAd()
      }, 1000 * this.errorCount)
    },

    startTimeout () {
      // 禁用组件级超时，交由懒加载系统控制
      /*
      this.timeoutTimer = setTimeout(() => {
        if (this.adStatus === AD_STATUS.PENDING) {
          this.handleAdError(new Error('Ad load timeout'))
          this.isLoading = false
          this.shouldShowAd = false
        }
      }, this.timeout)
      */
    },

    clearTimeout () {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer)
        this.timeoutTimer = null
      }
    },

    resetState () {
      Object.assign(this.$data, {
        isAdFilled: true,
        isLoading: true,
        adStatus: AD_STATUS.PENDING,
        loadTime: 0,
        errorCount: 0,
        shouldShowAd: true
      })
      this.clearTimers()
    },

    clearTimers () {
      [this.timeoutTimer, this.renderTimeout, this.noFillTimer].forEach(timer => {
        timer && clearTimeout(timer)
      })
      this.timeoutTimer = this.renderTimeout = this.noFillTimer = null
    },

    clearNoFillTimer () {
      if (this.noFillTimer) {
        clearTimeout(this.noFillTimer)
        this.noFillTimer = null
      }
    }
  }
}
</script>

<style scoped>
.ads-loading {
	min-height: 100px;
}

.ads-item {
	position: relative;
	transition: opacity 0.3s;

	&.ads-loading {
		opacity: 0.6;
	}

	&.ads-item--collapsed {
		height: 0 !important;
		min-height: 0 !important;
		max-height: 0 !important;
		margin: 0 !important;
		padding: 0 !important;
		overflow: hidden !important;
		visibility: hidden !important;

		.ads-borded {
			height: 0 !important;
			min-height: 0 !important;
			max-height: 0 !important;
			margin: 0 !important;
			padding: 0 !important;
			border: 0 !important;
		}
	}
}

.ad-msg {
	position: absolute;
	bottom: 0;
	right: 0;
	background: rgba(0, 0, 0, 0.7);
	color: white;
	padding: 4px 8px;
	font-size: 12px;
	border-radius: 4px;
}
</style>
